import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"
import disableAiEngineMutation from "__generated__/queries/mutations/disableAiEngine"
import enableAiEngineMutation from "__generated__/queries/mutations/enableAiEngine"
import pinAiEngineMutation from "__generated__/queries/mutations/pinAiEngine"
import rollbackAiEngineRoutingMutation from "__generated__/queries/mutations/rollbackAiEngineRouting"
import setAiEngineDefaultMutation from "__generated__/queries/mutations/setAiEngineDefault"
import unpinAiEngineMutation from "__generated__/queries/mutations/unpinAiEngine"
import getAiEngineRoutingQuery from "__generated__/queries/queries/getAiEngineRouting"

export type AiEngineSummary = {
  version: string
  capabilities: string[]
  frozen: boolean
}

export type AiEngineDefault = { capability: string; version: string }

export type AiEnginePin = {
  customerId: string
  capability?: string | null
  version: string
  note?: string | null
  createdBy: string
  createdAt: string
}

export type AiEngineDisabled = {
  capability: string
  version: string
  reason: string
  createdBy: string
  createdAt: string
}

export type AiEngineRoutingSnapshot = {
  defaults: AiEngineDefault[]
  pins: AiEnginePin[]
  disabled: AiEngineDisabled[]
}

export type AiEngineRoutingHistoryEntry = {
  index: number
  at: string
  by: string
  reason: string
  snapshot: AiEngineRoutingSnapshot
}

export type AiEngineRouting = AiEngineRoutingSnapshot & {
  history: AiEngineRoutingHistoryEntry[]
  updatedBy: string
  updatedAt: string
}

export type AiEngineRoutingOverview = {
  engines: AiEngineSummary[]
  capabilities: string[]
  routing: AiEngineRouting
}

type OverviewResponse = {
  success?: boolean | null
  code?: string | null
  message?: string | null
  data?: AiEngineRoutingOverview | null
}

const unwrap = (
  response: ApiFormattedResponse<Record<string, OverviewResponse | undefined>>,
  field: string
): ApiFormattedResponse<AiEngineRoutingOverview> => {
  if (!response.isSuccess) {
    return { isSuccess: false, errorMessage: response.errorMessage }
  }
  const payload = response.data?.[field]
  if (!payload?.success || !payload.data) {
    return {
      isSuccess: false,
      errorMessage: payload?.message || payload?.code || "Request failed"
    }
  }
  const overview: AiEngineRoutingOverview = payload.data
  return { isSuccess: true, data: overview }
}

export async function getAiEngineRouting(): Promise<
  ApiFormattedResponse<AiEngineRoutingOverview>
> {
  const response = await query<Record<string, OverviewResponse | undefined>>({
    query: getAiEngineRoutingQuery,
    fetchPolicy: "network-only"
  })
  return unwrap(response, "getAiEngineRouting")
}

const runMutation = async (
  document: Parameters<typeof mutate>[0]["mutation"],
  field: string,
  data: Record<string, unknown>
): Promise<ApiFormattedResponse<AiEngineRoutingOverview>> => {
  const response = await mutate<Record<string, OverviewResponse | undefined>>({
    mutation: document,
    variables: { data }
  })
  return unwrap(response, field)
}

export type AiEngineReason = { reason: string }

export const setAiEngineDefault = (
  data: AiEngineReason & { capability: string; version: string }
) => runMutation(setAiEngineDefaultMutation, "setAiEngineDefault", data)

export const pinAiEngine = (
  data: AiEngineReason & {
    customerId: string
    version: string
    capability?: string
    note?: string
  }
) => runMutation(pinAiEngineMutation, "pinAiEngine", data)

export const unpinAiEngine = (
  data: AiEngineReason & { customerId: string; capability?: string }
) => runMutation(unpinAiEngineMutation, "unpinAiEngine", data)

export const disableAiEngine = (
  data: AiEngineReason & { capability: string; version: string }
) => runMutation(disableAiEngineMutation, "disableAiEngine", data)

export const enableAiEngine = (
  data: AiEngineReason & { capability: string; version: string }
) => runMutation(enableAiEngineMutation, "enableAiEngine", data)

export const rollbackAiEngineRouting = (
  data: AiEngineReason & { historyIndex: number }
) =>
  runMutation(rollbackAiEngineRoutingMutation, "rollbackAiEngineRouting", data)

/** `v2` > `v1`: the version list is shown newest first. */
export const compareEngineVersion = (a: string, b: string) =>
  Number(b.replace(/^v/, "")) - Number(a.replace(/^v/, ""))

/** The version a capability actually runs on when no pin applies. */
export const effectiveDefault = (
  routing: AiEngineRoutingSnapshot,
  capability: string
) =>
  routing.defaults.find((entry) => entry.capability === capability)?.version ??
  "-"

export const isDisabled = (
  routing: AiEngineRoutingSnapshot,
  capability: string,
  version: string
) =>
  routing.disabled.some(
    (entry) => entry.capability === capability && entry.version === version
  )
